import cropr, { RecordModel } from "../../shared/utils/cropr";
import { allCropsId, myCropsItemType } from "./useGroupedCrops";
import { useMemo } from "react";
import useCroprBackbone from "../../shared/hooks/useCroprBackbone";

export type ProcessingStateType = "performed" | "planned" | "adviced" | "concept";

export type ActivityObj = {
  label?: string;
  count?: number;
  area?: number;
};

export type RecordsObj = Record<ProcessingStateType, Record<string, ActivityObj>>;
export type ActivityType = {
  inConfig: boolean;
  performedArea: number;
  plannedArea: number;
  advicedArea: number;
  conceptArea: number;
  cropfields: Map<number, number>;
  records: RecordsObj;
};
type ActivitiesPerCropType = {
  [index: string]: {
    [index: string]: ActivityType;
  };
};
const defaultThemes = [4, 5, 6, 7, 8, 9, 10];

const initActivitiesPerCrop = (myCrops: Map<string, myCropsItemType>): ActivitiesPerCropType => {
  const activitiesPerCrop: ActivitiesPerCropType = {};
  Array.from(myCrops).forEach(([cropId]) => {
    activitiesPerCrop[cropId] = {};
    defaultThemes.forEach((themeId) => {
      activitiesPerCrop[cropId][themeId] = {
        inConfig: [undefined, true].includes(cropr.getConfig("recording", themeId, true)),
        performedArea: 0,
        plannedArea: 0,
        advicedArea: 0,
        conceptArea: 0,
        cropfields: new Map<number, number>(),
        records: { performed: {}, planned: {}, concept: {}, adviced: {} },
      };
    });
  });
  return activitiesPerCrop;
};
export const useGroupedActivities = (
  myCrops: Map<string, myCropsItemType>
): ActivitiesPerCropType => {
  const [, recordsCollection] = useCroprBackbone<RecordModel>("records");
  const myRecords = useMemo(
    () =>
      recordsCollection
        .filter((r) => r.getRelated("cropfield", "crop")) //ignore records on fields without crop
        .filter((r) => !Boolean(r.get("linked_record"))) //ignore performed planning
        .map((r) => [
          r,
          r.getRelated("cropfield").id,
          r.getRelated("cropfield", "crop")?.id,
          r.get("activities").map((a: any) => a.theme),
          r.get("worked_area"),
          r.get("processing_state"),
        ]),
    [recordsCollection]
  );
  return useMemo(() => {
    const activitiesPerCrop = initActivitiesPerCrop(myCrops);
    myRecords.forEach(([record, cropfieldId, cropId, themes, area, recordProcessingState]) => {
      // @ts-ignore
      [...new Set(themes)].forEach((themeId: number) => {
        //skip if not default theme
        const activitiesForTheme = activitiesPerCrop[cropId]?.[themeId];
        if (!activitiesForTheme) return;
        var prefixType = "performed";
        if (recordProcessingState === "planned") {
          activitiesForTheme.plannedArea += area;
          prefixType = "planned";
        } else if (recordProcessingState === "adviced") {
          activitiesForTheme.advicedArea += area;
          prefixType = "adviced";
        } else if (recordProcessingState === "concept") {
          activitiesForTheme.conceptArea += area;
          prefixType = "concept";
        } else if (recordProcessingState === "performed") {
          activitiesForTheme.performedArea += area;
        }
        activitiesForTheme.cropfields.set(cropfieldId, cropfieldId);
        const activities = record.get("activities").filter((a: any) => a.theme === themeId);
        const currentRecords = activitiesForTheme.records;
        const activitiesDict = {};
        activities.forEach((activity: any) => {
          //@ts-ignore
          if (!activitiesDict[themeId]) {
            //@ts-ignore
            activitiesDict[themeId] = {
              label: cropr.detailtype[activity.type]?.name,
              type: activity.type,
              count: 1,
              area: area,
            };
          } else {
            //@ts-ignore
            activitiesDict[themeId]["label"] += " + " + cropr.detailtype[activity.type].name;
            // @ts-ignore
            activitiesDict[themeId]["type"] += "_" + activity.type;
          }
        });
        for (var key in activitiesDict) {
          // @ts-ignore
          const value = activitiesDict[key];
          // @ts-ignore
          if (!currentRecords[prefixType][value.type]) {
            // @ts-ignore
            currentRecords[prefixType][value.type] = {
              label: value.label,
              count: 0,
              area: 0,
            };
          }
          // @ts-ignore
          currentRecords[prefixType][value.type]["count"] += value.count;
          // @ts-ignore
          currentRecords[prefixType][value.type]["area"] += value.area;
        }
        activitiesPerCrop[cropId][themeId].records = currentRecords;
      });
    });

    //Calculate totals and save under allCropsId
    Object.values(activitiesPerCrop).forEach((activityObj) => {
      Object.entries(activityObj).forEach(([themeId, activityInfo]) => {
        const acs: ProcessingStateType[] = ["planned", "performed", "adviced", "concept"];
        const activitiesForCrop = activitiesPerCrop[allCropsId][themeId];
        for (const processingState of acs) {
          // @ts-ignore
          activitiesForCrop[processingState + "Area"] += activityInfo[processingState + "Area"];
        }
        activityInfo.cropfields.forEach((cropfieldId) =>
          activitiesForCrop.cropfields.set(cropfieldId, cropfieldId)
        );
        const currentRecords = activitiesPerCrop[allCropsId][themeId].records;
        acs.forEach((processingState) => {
          if (Object.keys(activityInfo.records[processingState]).length) {
            Object.entries(activityInfo.records[processingState]).forEach((entry) => {
              const [key, value] = entry;
              let rec = currentRecords[processingState][key];
              if (!rec) {
                rec = currentRecords[processingState][key] = {
                  label: value["label"],
                  count: 0,
                  area: 0,
                };
              }
              if (rec["count"] !== undefined && value["count"] !== undefined)
                rec["count"] += value["count"];
              if (rec["area"] !== undefined && value["area"] !== undefined)
                rec["area"] += value["area"];
            });
          }
        });
      });
    });

    return activitiesPerCrop;
  }, [myCrops, myRecords]);
};
